/**
 * Smart Collection rule vocabulary — the fields a collection's rule may
 * reference, and how to read them off a raw Item.
 *
 * SCOPED v1 LIMITATION (explicit, not a silent cap — PRD §5 "no silent
 * caps"): rule evaluation runs outside the item-list React tree (it has to —
 * a collection's live membership is computed by the store, not a rendered
 * list), so it only has access to plain `Item` records, not the item list's
 * enriched `ItemRow` (which adds relationship/pool/session/collection
 * membership booleans via several cross-store joins computed in
 * useItemListData.ts). Relationship-derived fields (hasCluster, inItemPool,
 * cycleId/trackId/sessionId, catchballStatus, collections, isLeadershipApproved,
 * hasLineage, …) are therefore NOT in this vocabulary yet — a rule can't
 * reference "items in this item pool" or "items with a cluster relationship".
 * Every field below reads directly off Item with no cross-store join, so
 * what IS offered is fully correct. Widening this (Phase 2's server-side
 * evaluation is the natural place, since Postgres can join everything in one
 * query) is tracked, not silently dropped — see
 * LARGE-SETS-QUERY-AND-SMART-SETS-PRD.md Part 8 Phase 2.
 */

import type { Item } from '../../types/prioritization/lifecycle';
import type { PqlFieldDefInput } from '../pql/catalog';

export const SMART_COLLECTION_FIELDS: PqlFieldDefInput[] = [
  {
    field: 'itemCategory',
    label: 'Category',
    type: 'set',
    options: [
      { value: 'problem', label: 'Problem' },
      { value: 'opportunity', label: 'Opportunity' },
      { value: 'risk', label: 'Risk' },
      { value: 'solution', label: 'Solution' },
      { value: 'commitment', label: 'Commitment' },
      { value: 'investment', label: 'Investment' },
    ],
  },
  {
    field: 'exit_status',
    label: 'Status',
    type: 'set',
    options: [
      { value: 'active', label: 'Active' },
      { value: 'deferred', label: 'Deferred' },
      { value: 'archived', label: 'Archived' },
    ],
  },
  {
    field: 'altitudeLevel',
    label: 'Altitude',
    type: 'set',
    options: [1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: `Level ${n}` })),
  },
  { field: 'tags', label: 'Tags', type: 'set', options: [] },
  { field: 'source', label: 'Source', type: 'text' },
  { field: 'strategicBucket', label: 'Strategic bucket', type: 'text' },
  { field: 'title', label: 'Title', type: 'text' },
  { field: 'description', label: 'Description', type: 'text' },
  { field: 'createdAt', label: 'Created', type: 'date-range' },
  { field: 'updatedAt', label: 'Updated', type: 'date-range' },
  { field: 'createdBy', label: 'Created by', type: 'text' },
  { field: 'dedupeStatus', label: 'Dedupe status', type: 'text' },
  { field: 'isStrategicallyNominated', label: 'Strategically nominated', type: 'boolean' },
  { field: 'metaCategory', label: 'Subject type', type: 'text' },
];

/** Field resolver matching SMART_COLLECTION_FIELDS exactly — no cross-store data. */
export function getSmartCollectionFieldValue(item: Item, field: string): unknown {
  switch (field) {
    case 'itemCategory': return item.itemCategory;
    case 'exit_status': return item.exit_status ?? 'active';
    case 'altitudeLevel': return item.altitudeLevel != null ? String(item.altitudeLevel) : undefined;
    case 'tags': return item.tags;
    case 'source': return item.source;
    case 'strategicBucket': return item.strategicBucket;
    case 'title': return item.title;
    case 'description': return item.description;
    case 'createdAt': return item.createdAt;
    case 'updatedAt': return item.updatedAt;
    case 'createdBy': return item.createdBy;
    case 'dedupeStatus': return item.dedupeStatus;
    case 'isStrategicallyNominated': return item.isStrategicallyNominated;
    case 'metaCategory': return item.metaCategory;
    default:
      if (field.startsWith('meta:')) return item.metadata?.[field.slice(5)];
      return undefined;
  }
}
